import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Filter, Columns3, FileDown, Bell, GitBranch, Sprout, Droplets, HeartPulse, GraduationCap, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";

const FEATURE_ICONS = [Filter, Columns3, FileDown, Bell, GitBranch];
const VILLAGE_ICONS = [Sprout, Droplets, HeartPulse, GraduationCap, Home];
const VILLAGE_VII = ["x, iv", "i, iv", "i", "ii", "x"];

const C = {
  en: {
    h1: "Find partners your CSR committee will actually approve.",
    intro: "Since 14 July 2025, the rules changed. An implementing agency now needs 12A or 10(23C) registration, a three-year track record in comparable work, and a current CSR-1 registration with the MCA — certified by a practising CA, CS or Cost Accountant. NGOs that don't meet this can't take your CSR money directly, however good their work is.",
    warn: "Most directories won't tell you that until you've already spent three weeks on a shortlist.",
    cta1: "Browse eligible partners", cta2: "Create a CSR workspace",
    features: [
      ["Filter by eligibility", "Schedule VII head, district, budget band, and CSR-1 eligibility status — in one click."],
      ["Compare up to five", "Side-by-side comparison of credentials, financials and verification tier."],
      ["One-click due-diligence PDF", "Credentials, validity dates, three-year financials, governance, verification trail and site-visit photos."],
      ["Saved searches with alerts", "Get notified when a new eligible partner appears in your district."],
      ["Choose your model", "Execute the project yourself, or fund through a registered implementing agency — the results differ."],
    ],
    villageKicker: "Integrated programmes",
    villageT: "The Model Village — one budget, five outcomes",
    villageSub: "CSR that compounds. Fund a cluster of vetted partners in a single gram panchayat so agriculture, water, health, education and local livelihoods reinforce one another — the Indian gram-vikas approach, every strand mapped to Schedule VII.",
    village: [["Agriculture", "Natural farming, seed banks, FPOs"], ["Water", "Ponds, recharge, safe drinking water"], ["Health", "Camps, mobile clinics, nutrition"], ["Education", "Schools, scholarships, digital labs"], ["Village Development", "Roads, sanitation, self-help groups"]],
    villageCta: "Assemble a village programme",
    closeT: "No fee. No commission. No introduction charge.",
    closeB: "We don't sit between you and your partner, and we never will.",
  },
  ta: {
    h1: "உங்கள் CSR குழு உண்மையில் அங்கீகரிக்கும் கூட்டாளர்களைக் கண்டறியுங்கள்.",
    intro: "14 ஜூலை 2025 முதல் விதிகள் மாறின. ஒரு செயலாக்க அமைப்பிற்கு இப்போது 12A அல்லது 10(23C) பதிவு, ஒப்பிடத்தக்க பணியில் மூன்று ஆண்டு அனுபவம், மற்றும் நடப்பு CSR-1 பதிவு (பயிற்சி CA/CS/Cost Accountant சான்றளித்தது) தேவை. இதைப் பூர்த்தி செய்யாத என்.ஜி.ஓ-க்கள் எவ்வளவு நல்ல பணி செய்தாலும் நேரடி CSR நிதியைப் பெற முடியாது.",
    warn: "பெரும்பாலான அடைவுகள், நீங்கள் மூன்று வாரங்கள் செலவழித்த பின்னரே இதைச் சொல்லும்.",
    cta1: "தகுதியுள்ள கூட்டாளர்கள்", cta2: "CSR பணியிடம் உருவாக்கு",
    features: [
      ["தகுதியால் வடிகட்டு", "அட்டவணை VII, மாவட்டம், நிதி வரம்பு, CSR-1 தகுதி — ஒரே கிளிக்கில்."],
      ["ஐந்து வரை ஒப்பிடு", "சான்றுகள், நிதிநிலை, சரிபார்ப்பு நிலை — பக்கம் பக்கமாக ஒப்பீடு."],
      ["ஒரே கிளிக் ஆவணத் தொகுப்பு", "சான்றுகள், செல்லுபடி தேதிகள், மூன்று ஆண்டு நிதி, நிர்வாகம், சரிபார்ப்பு பாதை, முகாம் புகைப்படங்கள்."],
      ["எச்சரிக்கையுடன் சேமித்த தேடல்கள்", "உங்கள் மாவட்டத்தில் புதிய தகுதியுள்ள கூட்டாளர் வரும்போது அறிவிப்பு."],
      ["உங்கள் மாதிரியைத் தேர்வு செய்யுங்கள்", "நீங்களே செயல்படுத்துங்கள், அல்லது பதிவுசெய்த அமைப்பு மூலம் நிதியளியுங்கள் — முடிவுகள் வேறுபடும்."],
    ],
    villageKicker: "ஒருங்கிணைந்த திட்டங்கள்",
    villageT: "மாதிரி கிராமம் — ஒரு நிதி, ஐந்து விளைவுகள்",
    villageSub: "பெருகும் CSR. ஒரே ஊராட்சியில் சரிபார்க்கப்பட்ட கூட்டாளர்களுக்கு நிதியளியுங்கள் — வேளாண்மை, நீர், சுகாதாரம், கல்வி, வாழ்வாதாரம் ஒன்றையொன்று வலுப்படுத்தும். இந்திய கிராம வளர்ச்சி அணுகுமுறை, ஒவ்வொன்றும் அட்டவணை VII உடன் இணைக்கப்பட்டது.",
    village: [["வேளாண்மை", "இயற்கை வேளாண்மை, விதை வங்கிகள், FPO"], ["நீர்", "குளங்கள், நிரப்பல், பாதுகாப்பான குடிநீர்"], ["சுகாதாரம்", "முகாம்கள், நடமாடும் மருத்துவம், ஊட்டச்சத்து"], ["கல்வி", "பள்ளிகள், உதவித்தொகை, டிஜிட்டல் ஆய்வகங்கள்"], ["கிராம வளர்ச்சி", "சாலைகள், துப்புரவு, சுயஉதவிக் குழுக்கள்"]],
    villageCta: "ஒரு கிராம திட்டத்தை உருவாக்கு",
    closeT: "கட்டணம் இல்லை. கமிஷன் இல்லை. அறிமுகக் கட்டணம் இல்லை.",
    closeB: "உங்களுக்கும் உங்கள் கூட்டாளருக்கும் இடையே நாங்கள் நிற்பதில்லை, என்றும் நிற்க மாட்டோம்.",
  },
  hi: {
    h1: "ऐसे साझेदार खोजें जिन्हें आपकी CSR समिति सचमुच स्वीकृत करेगी।",
    intro: "14 जुलाई 2025 से नियम बदल गए। एक कार्यान्वयन एजेंसी को अब 12A या 10(23C) पंजीकरण, तुलनीय काम में तीन-वर्ष का ट्रैक रिकॉर्ड, और MCA के साथ वर्तमान CSR-1 पंजीकरण चाहिए — जो अभ्यासरत CA/CS/Cost Accountant द्वारा प्रमाणित हो। जो एनजीओ यह पूरा नहीं करते, चाहे उनका काम कितना भी अच्छा हो, आपका CSR पैसा सीधे नहीं ले सकते।",
    warn: "अधिकांश निर्देशिकाएँ यह तब तक नहीं बतातीं जब तक आप तीन हफ़्ते शॉर्टलिस्ट पर न लगा चुके हों।",
    cta1: "पात्र साझेदार देखें", cta2: "CSR कार्यक्षेत्र बनाएँ",
    features: [
      ["पात्रता से फ़िल्टर करें", "अनुसूची VII, ज़िला, बजट श्रेणी, और CSR-1 पात्रता — एक क्लिक में।"],
      ["पाँच तक तुलना करें", "प्रमाणपत्र, वित्त और सत्यापन स्तर की साथ-साथ तुलना।"],
      ["एक-क्लिक ड्यू-डिलिजेंस PDF", "प्रमाणपत्र, वैधता तिथियाँ, तीन-वर्ष का वित्त, शासन, सत्यापन ट्रेल और दौरे की तस्वीरें।"],
      ["अलर्ट के साथ सहेजी खोजें", "जब आपके ज़िले में नया पात्र साझेदार आए तो सूचना पाएँ।"],
      ["अपना मॉडल चुनें", "परियोजना स्वयं चलाएँ, या पंजीकृत एजेंसी के माध्यम से वित्त दें — परिणाम भिन्न होते हैं।"],
    ],
    villageKicker: "एकीकृत कार्यक्रम",
    villageT: "मॉडल गाँव — एक बजट, पाँच परिणाम",
    villageSub: "CSR जो कई गुना बढ़े। एक ग्राम पंचायत में जाँचे-परखे साझेदारों के समूह को वित्त दें ताकि कृषि, जल, स्वास्थ्य, शिक्षा और स्थानीय आजीविका एक-दूसरे को मज़बूत करें — भारतीय ग्राम-विकास दृष्टिकोण, हर धारा अनुसूची VII से जुड़ी।",
    village: [["कृषि", "प्राकृतिक खेती, बीज बैंक, FPO"], ["जल", "तालाब, रिचार्ज, सुरक्षित पेयजल"], ["स्वास्थ्य", "शिविर, मोबाइल क्लिनिक, पोषण"], ["शिक्षा", "स्कूल, छात्रवृत्ति, डिजिटल लैब"], ["ग्राम विकास", "सड़कें, स्वच्छता, स्वयं-सहायता समूह"]],
    villageCta: "एक गाँव कार्यक्रम बनाएँ",
    closeT: "कोई शुल्क नहीं। कोई कमीशन नहीं। कोई परिचय शुल्क नहीं।",
    closeB: "हम आपके और आपके साझेदार के बीच नहीं बैठते, और कभी नहीं बैठेंगे।",
  },
};

export default function ForCSR() {
  const { lang } = useT();
  const c = C[lang] || C.en;
  return (
    <div>
      <section className="bg-slate-brand text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16">
          <h1 className="text-3xl sm:text-4xl font-extrabold">{c.h1}</h1>
          <p className="text-stone-300 mt-4 measure text-lg">{c.intro}</p>
          <p className="text-gold font-semibold mt-4">{c.warn}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button asChild className="bg-terracotta hover:bg-terracotta-hover" data-testid="csr-browse-btn"><Link to="/discover?csr_eligible=true">{c.cta1} <ArrowRight size={16} className="ml-1.5" /></Link></Button>
            <Button asChild variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white/10" data-testid="csr-signup-btn"><Link to="/register">{c.cta2}</Link></Button>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-16">
        <div className="grid sm:grid-cols-2 gap-6">
          {c.features.map(([t, b], i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <div key={i} className="bg-white rounded-xl border border-stone-200 p-6">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-terracotta-light text-terracotta mb-3"><Icon size={20} /></span>
                <h3 className="font-bold text-stone-900">{t}</h3>
                <p className="text-sm text-stone-600 mt-1">{b}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-14">
          <span className="text-xs uppercase tracking-widest text-gold font-semibold">{c.villageKicker}</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-brand mt-2">{c.villageT}</h2>
          <p className="text-stone-600 mt-2 measure">{c.villageSub}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6">
            {c.village.map(([t, d], i) => {
              const Icon = VILLAGE_ICONS[i];
              return (
                <div key={i} className="bg-white rounded-xl border border-stone-200 p-4">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-terracotta-light text-terracotta mb-2"><Icon size={20} /></span>
                  <div className="font-bold text-slate-brand text-sm">{t}</div>
                  <div className="text-[11px] text-gold font-semibold mt-0.5">Schedule VII ({VILLAGE_VII[i]})</div>
                  <div className="text-xs text-stone-500 mt-1">{d}</div>
                </div>
              );
            })}
          </div>
          <Button asChild className="mt-6 bg-terracotta hover:bg-terracotta-hover" data-testid="csr-model-village-btn"><Link to="/discover?csr_eligible=true">{c.villageCta} <ArrowRight size={16} className="ml-1.5" /></Link></Button>
        </div>

        <div className="mt-10 rounded-2xl bg-terracotta-light border border-terracotta-border p-8 text-center">
          <h2 className="text-xl font-bold text-slate-brand">{c.closeT}</h2>
          <p className="text-stone-600 mt-2">{c.closeB}</p>
        </div>
      </section>
    </div>
  );
}
